import React, { useEffect, useMemo, useState } from "react";
import { FormContext, useForm } from "react-hook-form";
import { Spacer, Icon, Button } from "@dspworkplace/ui";
import { getCompanyId, useAuth, useLocalStorage } from "../../../../Auth";
import { AccessCheck, IsGranted } from "../../../../security";
import { TabList } from "../../../../components/Tab";
import ReminderCards from "./ReminderCards";
import { DEFAULT_REMINDER_VALUES } from "./constants";
import { fetchPayrollReminderSettings, savePayrollReminder } from "./api";
import { useSelectedStation } from "../../../../components/StationSelector/StationPicker";
import Loading, { LoadingWrapper } from "../../../../components/Loading";
import { PunchForm, TabWrapper, WarningWrapper } from "./styled";

const PunchRemindersActivePayroll = () => {

    const companyId = getCompanyId();
    const allStationsId = `${companyId}-all`;

    const { api } = useAuth();
    const [selectedStation] = useSelectedStation();
    const formMethods = useForm({
        defaultValues: DEFAULT_REMINDER_VALUES,
    });

    const [stationSelected, setStationSelected] = useState(allStationsId);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [stations] = useLocalStorage("stations");
    const { handleSubmit, reset } = formMethods;

    useEffect(() => {

        let ignore = false;

        const fetchPayrollSettings = async () => {
            setIsLoading(true);
            setLoadError(false);
            const stationOrAll = stationSelected || selectedStation;
            const result = await fetchPayrollReminderSettings(api, stationOrAll);

            if (ignore) return;

            if (result?.error) {
                setLoadError(true);
                setIsLoading(false);
                return;
            }
            setLoadError(false);

            const mergedSettings = Object.keys(DEFAULT_REMINDER_VALUES).reduce(
                (acc, key) => ({
                    ...acc,
                    [key]: {
                        ...DEFAULT_REMINDER_VALUES[key],
                        ...(result?.settings?.[key] ?? {}),
                    },
                }),
                {}
            );

            reset({
                ...mergedSettings,
                station: stationOrAll,
            });
            setIsLoading(false);
        };

        fetchPayrollSettings();

        return () => {
            ignore = true;
        };

    }, [api, reset, selectedStation, stationSelected]);

    const tabs = useMemo(() => ({
        all: {
            title: "All Stations",
            id: allStationsId,
            content: <ReminderCards station={allStationsId} />,
        },
        ...[...(stations || [])]
            .sort((a, b) => a?.code?.localeCompare(b?.code, undefined, { numeric: true }))
            .reduce((acc, station) => {
                acc[station?.code] = {
                    title: station?.code,
                    id: station?.id,
                    content: <ReminderCards station={station?.id} />,
                };
                return acc;
            }, {}),
    }), [allStationsId, stations]);

    const onSubmit = async (data) => {
        setSaveError(null);
        const { station, ...settings } = data;

        if (station === allStationsId && (!Array.isArray(stations) || stations.length === 0)) {
            setSaveError("The station list isn't loaded, so nothing was saved. Reload the page and save again.");
            return;
        }

        setIsLoading(true);
        try {
            if (station !== allStationsId) {
                const saved = await savePayrollReminder(api, station, settings);
                if (!saved) {
                    setSaveError("Settings weren't saved. Try saving again.");
                }
            } else {
                const targetIds = [...stations.map((s) => s.id), allStationsId];
                const results = await Promise.allSettled(
                    targetIds.map((id) => savePayrollReminder(api, id, settings))
                );
                const failedIds = targetIds.filter(
                    (_, i) => results[i].status === "rejected" || !results[i].value
                );
                if (failedIds.length) {
                    const failedNames = failedIds.map((id) =>
                        id === allStationsId
                            ? "All Stations"
                            : stations.find((s) => s.id === id)?.code ?? id
                    );
                    setSaveError(
                        `Settings weren't saved for ${failedNames.join(", ")}. These stations may now have different settings from the rest. Save again to update them.`
                    );
                }
            }
        } catch (error) {
            console.error(error);
            setSaveError("Settings couldn't be saved. Try saving again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormContext {...formMethods}>
            <PunchForm onSubmit={handleSubmit(onSubmit)}>
                <WarningWrapper>
                    <div>
                        <Icon.WarningV2 size="16px" color="none" strokeColor="#D97706" />
                    </div>
                    <div>
                        ADP and Paycom data are not real-time. A minimum delay of 15 minutes is enforced on all
                        reminders to account for the sync lag. Values below 15 min will be automatically adjusted.
                    </div>
                </WarningWrapper>
                <Spacer bottom={4} />
                {loadError ? (
                    <>
                        <WarningWrapper>
                            <div>
                                <Icon.WarningV2 size="16px" color="none" strokeColor="#D97706" />
                            </div>
                            <div>
                                Reminder settings couldn't be loaded for this station, so saving is turned off.
                                Reload the page to try again.
                            </div>
                        </WarningWrapper>
                        <Spacer bottom={4} />
                    </>
                ) : null}
                <TabWrapper>
                    {isLoading ? (
                        <LoadingWrapper>
                            <Loading />
                        </LoadingWrapper>
                    ) : null}
                    <TabList
                        defaultTab={"all"}
                        tabs={tabs}
                        onChange={(val) => {
                            const tab = tabs[val];
                            setStationSelected(tab?.id);
                        }}
                    />
                </TabWrapper>
                <IsGranted feature={AccessCheck.features.SETTINGS} ops={[AccessCheck.OPS.U]}>
                    {saveError ? (
                        <>
                            <Spacer bottom={4} />
                            <WarningWrapper>
                                <div>
                                    <Icon.WarningV2 size="16px" color="none" strokeColor="#D97706" />
                                </div>
                                <div>{saveError}</div>
                            </WarningWrapper>
                        </>
                    ) : null}
                    <Spacer bottom={6} />
                    <Button type='primary' Type='submit' disabled={isLoading || loadError}>Save</Button>
                </IsGranted>
                <Spacer bottom={20} />
            </PunchForm>
        </FormContext>
    );
};

export default PunchRemindersActivePayroll;